import { PageHeader } from 'components'
import { useAuth } from 'context'
import { AppSecondaryButton } from 'elements'
import { GetApprovalReportDetail, GetApprovalReportHistory } from 'hooks'
import { useNotification } from 'hooks/toast'
import {
    APPROVAL_STATUS,
    ApprovalDetailView,
    ApprovalHistory,
    canApproveReports,
    extractApiError,
    normalizeApprovalListResponse,
    ScreenContainer,
} from 'page-components/report-approval'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { isApprovalAdmin, ReportApprovalService } from 'utility'

// The report is waiting for the caller when it is in their "awaiting me" queue (the CURRENT approval step's approver).
const loadAwaitingMe = async completedReportId => {
    try {
        const result = await ReportApprovalService.listPendingReports({
            page: 1,
            size: 200,
            status: APPROVAL_STATUS.AwaitingMe,
        })
        const items = normalizeApprovalListResponse(result).items || []
        return items.some(
            item =>
                String(item.completedReportId) === String(completedReportId),
        )
    } catch {
        return false
    }
}

export const ReportApprovalDetailScreen = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const { user } = useAuth()
    const { data, loading, setData, refresh } = GetApprovalReportDetail(id)
    const {
        data: history,
        loading: historyLoading,
        refresh: refreshHistory,
        pendingStep,
    } = GetApprovalReportHistory(id)
    const [awaitingMe, setAwaitingMe] = useState(false)

    // admins and approvers named in an approval workflow (login CanApproveReports)
    const allowed = useMemo(() => canApproveReports(user), [user])
    const approvalAdmin = useMemo(() => isApprovalAdmin(user), [user])

    useEffect(() => {
        let cancelled = false
        setAwaitingMe(false)
        if (allowed && id) {
            loadAwaitingMe(id).then(value => {
                if (!cancelled) setAwaitingMe(value)
            })
        }
        return () => {
            cancelled = true
        }
    }, [allowed, id])

    const handleChainAction = useCallback(async () => {
        setAwaitingMe(await loadAwaitingMe(id))
        await Promise.all([refresh(), refreshHistory()]).catch(() => {})
    }, [id, refresh, refreshHistory])

    // admins: e-mail the current approver a new no-login link (the previous one stops working)
    const { successToast, errorToast } = useNotification()
    const [resending, setResending] = useState(false)
    const handleResend = useCallback(async () => {
        try {
            setResending(true)
            const res = await ReportApprovalService.resendApprovalLink(id)
            const who = res?.ApproverName || res?.approverName
            successToast(
                who
                    ? `Approval e-mail sent to ${who}.`
                    : 'Approval e-mail sent.',
            )
        } catch (err) {
            // 502 MAIL_FAILED: a new link exists but the e-mail did not leave - the server message says so
            errorToast(extractApiError(err))
        } finally {
            setResending(false)
        }
    }, [id, successToast, errorToast])
    // only while an approval step is waiting (history OverallStatus 0 + CurrentStep)
    const showResend =
        approvalAdmin &&
        !!data &&
        data.status !== APPROVAL_STATUS.Approved &&
        pendingStep != null

    if (!allowed) {
        return <Navigate to="/app/task" replace />
    }

    return (
        <ScreenContainer>
            <PageHeader header="Report Approval" />
            <ApprovalDetailView
                report={data}
                loading={loading}
                onBack={() => navigate('/app/task')}
                onApproved={updated => setData(updated)}
                isAdmin={approvalAdmin}
                awaitingMe={awaitingMe}
                onChainAction={handleChainAction}
            />
            <div className="mt-3">
                <ApprovalHistory events={history} loading={historyLoading} />
            </div>
            {showResend && (
                <div className="mt-2">
                    <AppSecondaryButton
                        type="button"
                        onClick={handleResend}
                        disabled={resending}
                        title="E-mail the current approver a new approval link (the previous link stops working)"
                    >
                        {resending ? 'Sending…' : 'Resend approval e-mail'}
                    </AppSecondaryButton>
                </div>
            )}
        </ScreenContainer>
    )
}
